/* Homepage video feed (sections/pocket-reels.liquid).
   Cards play muted while on screen and pause when they leave it. Tapping opens a full-screen viewer.
   Sound never starts by itself: the viewer always opens muted, with a "Tap for sound" button. */
(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const saveData = navigator.connection?.saveData;
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  /* Instagram's official player for a post: its link with /embed/ on the end. */
  const embedUrl = (u) => u.replace(/[?#].*$/, '').replace(/\/?$/, '/') + 'embed/';

  function init(root) {
    if (root.dataset.prlReady) return;
    root.dataset.prlReady = '1';
    const cards = [...root.querySelectorAll('[data-prl-card]')];
    if (!cards.length) return;

    /* Autoplay: only cards that are mostly on screen, and not for people who asked for less motion or data. */
    const play = (v) => { const p = v.play(); if (p) p.catch(() => {}); };
    if (!reduced && !saveData && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => entries.forEach((e) => {
        const v = e.target.querySelector('video');
        if (!v) return;
        if (e.isIntersecting && !document.body.classList.contains('prl-lock')) play(v); else v.pause();
      }), { threshold: 0.6 });
      cards.forEach((c) => {
        const v = c.querySelector('video');
        if (!v) return;
        v.addEventListener('playing', () => c.classList.add('is-playing'));
        v.addEventListener('pause', () => c.classList.remove('is-playing'));
        io.observe(c);
      });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) cards.forEach((c) => c.querySelector('video')?.pause());
        else if (!document.body.classList.contains('prl-lock')) resumeVisible();
      });
    }
    /* Back from another app or the viewer: play what's on screen again (the observer only reports changes). */
    function resumeVisible() {
      if (reduced || saveData) return;
      cards.forEach((c) => {
        const v = c.querySelector('video'), r = c.getBoundingClientRect();
        const shown = Math.max(0, Math.min(r.right, innerWidth) - Math.max(r.left, 0)) * Math.max(0, Math.min(r.bottom, innerHeight) - Math.max(r.top, 0));
        if (v && shown >= 0.6 * r.width * r.height) play(v);
      });
    }

    /* Viewer */
    const viewer = document.createElement('div');
    viewer.className = 'prl-viewer color-background-1';
    viewer.setAttribute('role', 'dialog');
    viewer.setAttribute('aria-modal', 'true');
    viewer.setAttribute('aria-label', 'Videos');
    viewer.setAttribute('aria-hidden', 'true');
    viewer.innerHTML = `<button class="prl-viewer__btn prl-viewer__close" aria-label="Close">×</button>
      <button class="prl-viewer__btn prl-viewer__sound" aria-pressed="false">🔇 Tap for sound</button>
      <span class="prl-viewer__count" aria-live="polite"></span>
      <button class="prl-viewer__nav prl-viewer__nav--prev" aria-label="Previous video">‹</button>
      <button class="prl-viewer__nav prl-viewer__nav--next" aria-label="Next video">›</button>
      <div class="prl-track">${cards.map((c, i) => {
        const d = c.dataset;
        const foot = `<div class="prl-slide__foot"><span>${d.creator ? '@' + esc(d.creator) : ''}</span>${d.permalink ? `<a href="${esc(d.permalink)}" target="_blank" rel="noopener">View on Instagram</a>` : ''}</div>`;
        return d.embed
          ? `<div class="prl-slide prl-slide--embed" data-i="${i}">${d.poster ? `<img class="prl-slide__poster" src="${esc(d.poster)}" alt="">` : ''}${foot}</div>`
          : `<div class="prl-slide" data-i="${i}"><video playsinline loop preload="none" ${d.poster ? `poster="${esc(d.poster)}"` : ''}>
              ${d.hls ? `<source src="${esc(d.hls)}" type="application/x-mpegURL">` : ''}${d.mp4 ? `<source src="${esc(d.mp4)}" type="video/mp4">` : ''}</video>${foot}</div>`;
      }).join('')}</div>`;
    document.body.append(viewer);
    const track = viewer.querySelector('.prl-track');
    const slides = [...track.children];
    const count = viewer.querySelector('.prl-viewer__count');
    const soundBtn = viewer.querySelector('.prl-viewer__sound');
    let current = -1, muted = true, lastFocus = null;

    /* Once someone turns sound on, it stays on as they swipe, until the viewer closes. */
    const setSound = () => {
      soundBtn.textContent = muted ? '🔇 Tap for sound' : '🔊';
      soundBtn.setAttribute('aria-pressed', String(!muted));
      soundBtn.setAttribute('aria-label', muted ? 'Turn sound on' : 'Turn sound off');
      soundBtn.classList.toggle('is-on', !muted);
      slides.forEach((s) => { const v = s.querySelector('video'); if (v) v.muted = muted; });
    };
    const show = (i) => {
      if (i === current) return;
      current = i;
      count.textContent = `${i + 1} / ${slides.length}`;
      slides.forEach((s, k) => {
        const v = s.querySelector('video');
        if (k === i) {
          if (v) {
            v.muted = muted;
            v.currentTime = 0;
            /* If the phone won't play with sound (e.g. low power mode), carry on muted. */
            const p = v.play();
            if (p) p.catch(() => { if (!v.muted && current === k) { muted = true; setSound(); play(v); } });
          }
          /* Instagram's player loads only when its slide is reached (its cover shows until then). */
          if (s.classList.contains('prl-slide--embed') && !s.querySelector('iframe')) {
            const f = document.createElement('iframe');
            f.src = embedUrl(cards[k].dataset.embed);
            f.title = 'Instagram video';
            f.allow = 'autoplay; encrypted-media; picture-in-picture';
            s.prepend(f);
            s.classList.add('has-player');
          }
        } else {
          v?.pause();
          /* Leaving an Instagram player: remove it so it can't keep playing out of sight. */
          const f = s.querySelector('iframe');
          if (f) { f.remove(); s.classList.remove('has-player'); }
        }
      });
      soundBtn.hidden = slides[i].classList.contains('prl-slide--embed');
    };
    const open = (i) => {
      lastFocus = document.activeElement;
      cards.forEach((c) => c.querySelector('video')?.pause());
      document.body.classList.add('prl-lock');
      viewer.classList.add('is-open');
      viewer.setAttribute('aria-hidden', 'false');
      current = -1;
      muted = true;
      setSound();
      track.scrollTo({ left: i * track.clientWidth, behavior: 'instant' });
      show(i);
      viewer.querySelector('.prl-viewer__close').focus({ preventScroll: true });
    };
    const close = () => {
      viewer.classList.remove('is-open');
      viewer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('prl-lock');
      slides.forEach((s) => s.querySelector('video')?.pause());
      /* Stop any Instagram player that's still running. */
      slides.forEach((s) => { s.querySelector('iframe')?.remove(); s.classList.remove('has-player'); });
      current = -1;
      lastFocus?.focus?.({ preventScroll: true });
      resumeVisible();
    };

    let t;
    track.addEventListener('scroll', () => { clearTimeout(t); t = setTimeout(() => show(Math.round(track.scrollLeft / track.clientWidth)), 80); }, { passive: true });
    const go = (d) => track.scrollTo({ left: Math.max(0, Math.min(slides.length - 1, current + d)) * track.clientWidth, behavior: 'smooth' });
    viewer.addEventListener('click', (e) => {
      if (e.target.closest('.prl-viewer__close')) return close();
      if (e.target.closest('.prl-viewer__sound')) { muted = !muted; return setSound(); }
      if (e.target.closest('.prl-viewer__nav--prev')) return go(-1);
      if (e.target.closest('.prl-viewer__nav--next')) return go(1);
      /* Tap a playing video to pause it, again to carry on. */
      const v = e.target.closest('video');
      if (v) { if (v.paused) play(v); else v.pause(); }
    });
    document.addEventListener('keydown', (e) => {
      if (!viewer.classList.contains('is-open')) return;
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') go(1);
      if (e.key === 'ArrowLeft') go(-1);
    });
    /* Swipe down to close, on video slides (Instagram's player handles its own touches). */
    let sy = 0, sx = 0;
    track.addEventListener('touchstart', (e) => { sy = e.touches[0].clientY; sx = e.touches[0].clientX; }, { passive: true });
    track.addEventListener('touchend', (e) => {
      const dy = e.changedTouches[0].clientY - sy, dx = e.changedTouches[0].clientX - sx;
      if (dy > 110 && Math.abs(dx) < 60) close();
    }, { passive: true });

    root.addEventListener('click', (e) => {
      const c = e.target.closest('[data-prl-card]');
      if (c) open(cards.indexOf(c));
    });
  }

  const start = () => document.querySelectorAll('[data-pocket-reels]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
  document.addEventListener('shopify:section:load', start);
})();
